<template>
  <header :class="headerClasses">
    <div
      class="mx-auto flex w-full items-start justify-between gap-4 pb-2 md:pb-2"
      :class="{ '!pb-0': isScrolled }"
    >
      <div class="mb-0 flex flex-1 items-center md:mb-2">
        <router-link to="/">
          <SiteLogo />
        </router-link>
      </div>
      <div class="mx-4 hidden flex-1 md:block">
        <MainSearch />
      </div>
      <div class="flex flex-1 items-center justify-end space-x-2">
        <Button
          v-if="!authStore.user"
          @click="openAuthModal()"
          label="Login"
          class="p-button-text h-[32px] w-[70px] rounded-md border border-zinc-500 text-[14px] !text-white hover:bg-transparent md:h-[33px] md:w-auto"
        />
        <Button
          v-if="!authStore.user"
          @click="openAuthModal()"
          label="Join"
          class="h-[32px] w-[70px] rounded-md text-[14px] md:h-[33px] md:w-auto"
        />
        <Select
          v-if="authStore.user"
          :options="userOptions"
          v-model="selectedNewOption"
          optionLabel="label"
          append-to="self"
          class="p-button-text user-profile rounded-full border-[3px] border-transparent !bg-transparent !p-0 pl-0 shadow-none hover:bg-transparent"
        >
          <template #value="">
            <div class="flex items-center gap-2">
              <div>
                <Avatar
                  v-if="authStore.user.avatar"
                  :image="authStore.user.avatar"
                  class="bg-white/30 p-[1px] md:h-[42px] md:w-[42px]"
                  shape="circle"
                />
                <Avatar
                  v-if="!authStore.user.avatar"
                  :label="`${authStore.user.email[0].toUpperCase()}`"
                  class="bg-primary text-xs font-semibold text-black md:h-[42px] md:w-[42px] md:text-sm"
                  shape="circle"
                />
              </div>
            </div>
          </template>
          <template #header>
            <div class="flex min-w-[320px] flex-col gap-3 border-b p-5">
              <div class="flex items-center gap-2">
                <div>
                  <Avatar
                    v-if="authStore.user.avatar && !avatarError"
                    :image="authStore.user.avatar"
                    @hook:mounted="onAvatarMounted"
                    class="bg-white/30 p-[1px] md:h-[42px] md:w-[42px]"
                    shape="circle"
                  />
                  <Avatar
                    v-if="!authStore.user.avatar"
                    :label="`${authStore.user.email[0].toUpperCase()}`"
                    class="bg-primary text-xs font-semibold text-black md:h-[42px] md:w-[42px] md:text-sm"
                    shape="circle"
                  />
                </div>
                <div
                  class="flex flex-col items-start justify-start text-sm leading-5 text-slate-500"
                >
                  <span class="text-xs">{{ authStore.user?.email }}</span>
                </div>
              </div>
            </div>
          </template>
          <template #option="slotProps">
            <div class="flex items-center px-2">
              <span :class="`text-[15px] font-bold text-${slotProps.option.color}-500`">{{
                slotProps.option.label
              }}</span>
            </div>
          </template>
        </Select>
      </div>
    </div>
    <div>
      <HeaderNav />
    </div>
  </header>
</template>

<script lang="ts" setup>
import { authApi } from "@/api/authApi";
import { useModalManager } from "@/composables/useModalManager";
import router from "@/router";
import { useAuthenticationStore } from "@/stores/useAuthenticationStore";
import { ModalEnum } from "@/types/enums";
import SiteLogo from "@/ui/components/SiteLogo.vue";
import HeaderNav from "@/ui/layouts/HeaderNav.vue";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import MainSearch from "./MainSearch.vue";

interface UserOption {
  label: string;
  icon?: unknown;
  color?: string;
  action?: () => void;
}

interface NewOption {
  label: string;
  action: () => void;
}

const { setActiveModal, setRedirectUrl } = useModalManager();
const authStore = useAuthenticationStore();
const route = useRoute();
const avatarError = ref(false);

const isScrolled = ref(false);
const selectedNewOption = ref<NewOption | null>(null);

const headerClasses = computed(() => {
  return route.name === "single-event"
    ? "top-0 z-50 flex flex-col border-b border-lime-400 bg-zinc-950 p-4 shadow-md transition-all duration-300" // non-sticky version for this page
    : "sticky top-0 z-50 flex flex-col border-b border-lime-400 bg-zinc-950 p-4 shadow-md transition-all duration-300"; // sticky on other pages
});

const logout = async () => {
  const status = await authApi.logout();
  if (status == 204) {
    authStore.logout();
    window.location.reload();
  }
};

const onAvatarMounted = (el: any) => {
  const img = el?.$el?.querySelector("img");
  if (img) {
    img.onerror = () => {
      avatarError.value = true;
    };
  }
};

watch(selectedNewOption, (newValue) => {
  if (newValue && newValue.action) {
    newValue.action();
    selectedNewOption.value = null;
  }
});

const goToProfile = () => {
  router.push({ name: "profile" });
};

const userOptions = ref<UserOption[]>([
  { label: "Profile", icon: null, action: goToProfile, color: "gray" },
  { label: "Log out", icon: null, action: logout, color: "rose" },
]);

const openAuthModal = (): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "header_join_btn",
  });
  setRedirectUrl(window.location.pathname);
  setActiveModal(ModalEnum.AuthModal);
};
</script>

<style scoped>
:deep(.p-avatar img) {
  height: 100% !important;
  width: 100% !important;
  object-fit: cover !important;
  aspect-ratio: 1 / 1 !important;
}
</style>
